<script setup>
const props = defineProps({
  show: Boolean,
  title: { type: String, default: "Cancelar compra" },
  subtitle: { type: String, default: "Esta acción revertirá el registro." },
  confirmText: { type: String, default: "Cancelar" },
  cancelText: { type: String, default: "Volver" },
  loading: { type: Boolean, default: false },
  message: { type: String, default: "" },
  confirmDisabled: { type: Boolean, default: false },
});
const emit = defineEmits(["close", "confirm"]);
function close() {
  emit("close");
}
function confirm() {
  emit("confirm");
}
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <header class="modal-header">
          <h2 class="modal-title">{{ title }}</h2>
          <button class="close-btn" @click="close" aria-label="Cerrar">
            <span class="material-symbols-rounded">close</span>
          </button>
        </header>
        <div class="modal-body">
          <p class="modal-subtitle">{{ subtitle }}</p>
          <p v-if="message" class="modal-message">{{ message }}</p>
          <div class="actions">
            <button
              class="btn cancel"
              :disabled="loading || confirmDisabled"
              @click="confirm"
            >
              <span class="material-symbols-rounded">cancel</span>
              {{ confirmText }}
            </button>
            <button class="btn ghost" :disabled="loading" @click="close">
              {{ cancelText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ===== MODAL OVERLAY ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* ===== MODAL CONTAINER ===== */
.modal-container {
  position: relative;
  background: var(--panel-bg);
  border: 1.5px solid var(--panel-border);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  color: var(--text);
  backdrop-filter: blur(20px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===== MODAL HEADER ===== */
.modal-header {
  padding: 2rem 2rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(239, 68, 68, 0.1),
    rgba(249, 115, 22, 0.05)
  );
  border-bottom: 1px solid var(--border);
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 900;
  margin: 0;
  text-align: center;
  background: linear-gradient(135deg, #ef4444, #f97316);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.close-btn {
  position: absolute;
  right: 1.25rem;
  top: 1.25rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1.5px solid transparent;
  color: var(--muted);
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
  color: #ef4444;
  transform: rotate(90deg) scale(1.1);
}

.close-btn .material-symbols-rounded {
  font-size: 1.25rem;
}

/* ===== MODAL BODY ===== */
.modal-body {
  padding: 1.75rem 2rem 2rem;
  text-align: center;
}

.modal-subtitle {
  color: var(--muted);
  font-size: 1rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  font-weight: 500;
}

.modal-message {
  color: var(--text);
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
  padding: 1rem;
  background: rgba(124, 92, 255, 0.08);
  border: 1px solid rgba(124, 92, 255, 0.2);
  border-radius: 12px;
  font-weight: 600;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== ACTION BUTTONS ===== */
.actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  border: none;
  border-radius: 14px;
  cursor: pointer;
  padding: 0.875rem 1.75rem;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  min-width: 140px;
  justify-content: center;
}

.btn::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.2));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.btn:hover::before {
  opacity: 1;
}

.btn.cancel {
  background: linear-gradient(135deg, #ef4444 0%, #f97316 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
}

.btn.cancel:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
}

.btn.cancel:active {
  transform: translateY(0);
}

.btn.cancel .material-symbols-rounded {
  font-size: 1.25rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.btn.ghost {
  background: var(--surface);
  color: var(--text);
  border: 1.5px solid var(--border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.btn.ghost:hover {
  background: var(--panel-bg);
  border-color: var(--accent);
  color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(124, 92, 255, 0.2);
}

.btn.ghost:active {
  transform: translateY(0);
}

/* ===== TRANSITIONS ===== */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 640px) {
  .modal-container {
    max-width: 100%;
    margin: 0 0.5rem;
    border-radius: 20px;
  }

  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .close-btn {
    right: 1rem;
    top: 1rem;
    padding: 0.5rem;
  }

  .modal-body {
    padding: 1.5rem 1.5rem 1.75rem;
  }

  .modal-subtitle {
    font-size: 0.95rem;
  }

  .actions {
    flex-direction: column;
    gap: 0.75rem;
  }

  .btn {
    width: 100%;
    min-width: auto;
    padding: 1rem 1.5rem;
  }
}

@media (max-width: 380px) {
  .modal-container {
    margin: 0 0.25rem;
  }

  .modal-header {
    padding: 1.25rem 1.25rem 0.875rem;
  }

  .modal-title {
    font-size: 1.125rem;
  }

  .modal-body {
    padding: 1.25rem;
  }
}
</style>
